<template>
  <div class="page-container">

    <div class="page-header">
      <div class="page-title">
        발주처 목록
        <span class="count-text">총 {{ resolvedTotalCount }} 건</span>
      </div>

      <div class="header-buttons">
        <button class="btn primary" @click="onClickCreate">
          신규 등록
        </button>
      </div>
    </div>

    <div class="content-wrapper">
      <div class="card client-list-card">

        <!-- 검색 영역 -->
        <div class="filter-card">
          <div class="filter-row">
            <div class="filter-field full-width">
              <label class="filter-label">검색</label>

              <div class="keyword-search-wrap">
                <select v-model="filters.searchType" class="control search-type-select">
                  <option value="name">발주처명</option>
                  <option value="managerName">담당자</option>
                  <option value="managerPhone">연락처</option>
                  <option value="address">주소</option>
                </select>

                <input
                  v-model.trim="filters.keyword"
                  class="control"
                  type="text"
                  placeholder="검색어를 입력하세요"
                  @keyup.enter="onClickSearch"
                />

                <div class="search-action-group">
                  <button class="btn primary search-btn" @click="onClickSearch">
                    검색
                  </button>

                  <button class="btn ghost reset-btn" @click="onClickReset">
                    초기화
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 테이블 -->
        <div class="table-section">
          <table v-if="tableRows.length" class="data-table">
            <thead>
              <tr>
                <th>발주처명</th>
                <th>담당자</th>
                <th>연락처</th>
                <th>주소</th>
                <th>현장 수</th>
                <th class="text-center">작업</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="row in tableRows" :key="row.id">
                <td class="td-wrap td-name" :title="row.name">
                  {{ row.name || '-' }}
                </td>

                <td>{{ row.managerName || '-' }}</td>

                <td>{{ row.managerPhone || '-' }}</td>

                <td class="td-wrap td-address" :title="fullAddress(row)">
                  {{ fullAddress(row) || '-' }}
                </td>

                <td>{{ row.siteCount ?? 0 }}</td>

                <td class="td-actions">
                  <div class="row-actions">
                    <button
                      class="btn xs primary"
                      @click="onClickDetail(row)"
                    >
                      상세보기
                    </button>

                    <button
                      class="btn xs danger outline"
                      @click="toggleStatus(row)"
                    >
                      삭제
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <div v-else class="table-empty">
            표시할 발주처 데이터가 없습니다
          </div>
        </div>

        <PaginationBar
          :currentPage="currentPage"
          :totalPages="totalPages"
          :groupSize="groupSize"
          @change-page="goToPage"
        />

      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api/axios'
import PaginationBar from '@/components/admin/PaginationBar.vue'

export default {
  name: 'ClientList',

  components: {
    PaginationBar
  },

  data() {
    return {
      tableRows: [],
      totalCount: 0,
      totalPages: 0,
      currentPage: 0,
      size: 15,
      groupSize: 5,
      loading: false,

      filters: {
        searchType: 'name',
        keyword: '',
      },
    }
  },

  computed: {
    resolvedTotalCount() {
      return this.totalCount || this.tableRows.length
    }
  },

  mounted() {
    this.fetchClientList()
  },

  methods: {
    async toggleStatus(row) {
      const firstOk = confirm(
        `"${row.name}" 발주처를 삭제하시겠습니까?\n\n삭제 후 목록에서 표시되지 않습니다.`
      )
      if (!firstOk) return

      const secondOk = confirm(
        `정말로 삭제하시겠습니까?\n\n삭제 시 복구가 불가합니다.\n계속 진행하려면 확인을 눌러주세요.`
      )
      if (!secondOk) return

      try {
        await api.post('/app/client/update', {
          id: row.id,
          deleted: true
        })

        alert('삭제 완료')
        await this.fetchClientList()
      } catch (e) {
        console.error(e)
        alert('처리 실패')
      }
    },

    async fetchClientList() {
      this.loading = true

      try {
        const params = {
          page: this.currentPage,
          size: this.size,
          orderBy: 'createdAtDesc',
          deleted: false
        }

        if (this.filters.keyword) {
          params[this.filters.searchType] = this.filters.keyword
        }

        const res = await api.get('/app/client/list', { params })
        const page = res.data?.result
        const rows = Array.isArray(page?.content) ? page.content : []

        this.totalCount = Number(page?.totalElements || 0)
        this.totalPages = Number(page?.totalPages || 0)
        this.currentPage = Number(page?.number || 0)

        this.tableRows = await Promise.all(
          rows.map(async row => ({
            ...row,
            siteCount: await this.fetchClientSiteCount(row.id)
          }))
        )
      } catch (e) {
        console.error(e)
        this.tableRows = []
        this.totalCount = 0
        this.totalPages = 0
      } finally {
        this.loading = false
      }
    },

    async fetchClientSiteCount(clientId) {
      if (!clientId) return 0

      try {
        const res = await api.get('/app/client/sites', {
          params: { clientId }
        })

        const list = res.data?.result
        return Array.isArray(list) ? list.length : 0
      } catch (e) {
        console.error('발주처 연동 현장 수 조회 실패', e)
        return 0
      }
    },

    onClickSearch() {
      this.currentPage = 0
      this.fetchClientList()
    },

    onClickReset() {
      this.filters.searchType = 'name'
      this.filters.keyword = ''
      this.currentPage = 0
      this.fetchClientList()
    },

    fullAddress(row) {
      return [row.address, row.addressDetail].filter(Boolean).join(' ')
    },

    goToPage(page) {
      if (page < 0 || page >= this.totalPages) return
      this.currentPage = page
      this.fetchClientList()
    },

    onClickCreate() {
      this.$emit('create')
    },

    onClickDetail(row) {
      this.$emit('detail', row)
    }
  }
}
</script>

<style scoped>
@import "@/styles/admin/admin-layout.css";
@import "@/styles/admin/admin-table.css";
@import "@/styles/admin/admin-button.css";
@import "@/styles/admin/admin-card.css";
@import "@/styles/admin/admin-pagination.css";
@import "@/styles/admin/admin-input.css";

.td-actions {
  text-align: center;
}

.row-actions {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: nowrap;
  white-space: nowrap;
}

.row-actions .btn {
  min-width: 64px;
  height: 28px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
}

.client-list-card {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

/* 검색 영역 */
.filter-card {
  margin-bottom: 20px;
  padding: 18px 20px;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: #f8fafc;
}

.filter-row {
  display: grid;
  gap: 16px;
}

.filter-field {
  min-width: 0;
}

.filter-field.full-width {
  width: 100%;
}

.filter-label {
  display: block;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 700;
  color: #374151;
}

.keyword-search-wrap {
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
}

.search-action-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.search-btn,
.reset-btn {
  min-width: 88px;
  height: 40px;
}

/* 테이블 */
.table-section {
  flex: 1;
}

.data-table {
  table-layout: auto;
  min-width: 100%;
}

.data-table th:nth-child(1),
.data-table td:nth-child(1) {
  width: 22%;
}

.data-table th:nth-child(2),
.data-table td:nth-child(2) {
  width: 100px;
}

.data-table th:nth-child(3),
.data-table td:nth-child(3) {
  width: 120px;
}

.data-table th:nth-child(4),
.data-table td:nth-child(4) {
  width: auto;
}

.data-table th:nth-child(5),
.data-table td:nth-child(5) {
  width: 80px;
}

.data-table th:nth-child(6),
.data-table td:nth-child(6) {
  width: 130px;
}

.td-wrap {
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
  word-break: keep-all;
  overflow-wrap: anywhere;
  line-height: 1.5;
}

.td-name {
  min-width: 180px;
}

.td-address {
  min-width: 360px;
  text-align: left;
}

.table-empty {
  min-height: 240px;
}

@media (max-width: 900px) {
  .keyword-search-wrap {
    grid-template-columns: 1fr;
  }

  .search-action-group {
    justify-content: flex-end;
  }
}

@media (max-width: 768px) {
  .data-table {
    min-width: 1100px;
  }
}

</style>